import { Menu as BaseMenu } from "@base-ui/react/menu";
import { Check } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router";
import { twMerge } from "tailwind-merge";
import Checkbox from "../Checkbox";
import {
    FLOATING_CHOSEN_ROW_CLASS,
    FLOATING_ROW_CLASS,
    FLOATING_ROW_GROUP_CLASS,
    FLOATING_SECTION_TITLE_CLASS,
    FLOATING_SEPARATOR_CLASS,
    FloatingRowContent,
    FloatingRowCount,
} from "../FloatingPanel";

type MenuItemProps = {
    children: ReactNode;
    icon?: ReactNode;
    /** A second line under the label. */
    description?: ReactNode;
    /** Shown at the end of the row. */
    count?: number;
    /** Shown at the end of the row after the count, such as a sort direction arrow. */
    trailing?: ReactNode;
    /** Marks the item as the one in use, such as the current sort, with the chosen-row background. */
    current?: boolean;
    destructive?: boolean;
    disabled?: boolean;
    /** Defaults to true. */
    closeOnSelect?: boolean;
} & (
    | {
          onSelect: () => void;
          href?: never;
      }
    | {
          /** In-app path. Renders as a link. */
          href: string;
          onSelect?: never;
      }
);

/** An action to run, or a page to go to with href. */
export const MenuItem = (props: MenuItemProps) => {
    const closeOnSelect = props.closeOnSelect !== false;
    const rowClass = twMerge(FLOATING_ROW_CLASS, props.current === true && "bg-stone-100");
    const ariaCurrent = props.current === true ? "true" : undefined;
    const count = props.count !== undefined ? <FloatingRowCount count={props.count} /> : null;
    const hasTrailing = count !== null || props.trailing !== undefined;

    // Row content shared by the link and action versions.
    const content = (
        <FloatingRowContent
            icon={props.icon}
            description={props.description}
            destructive={props.destructive}
            trailing={hasTrailing ? <>{count}{props.trailing}</> : undefined}
        >
            {props.children}
        </FloatingRowContent>
    );

    // Base UI links can't be disabled, so a disabled link renders as a disabled item.
    if (props.href !== undefined && props.disabled !== true) {
        return (
            <BaseMenu.LinkItem className={rowClass} aria-current={ariaCurrent} closeOnClick={closeOnSelect} render={<Link to={props.href} />}>
                {content}
            </BaseMenu.LinkItem>
        );
    }

    // Renders as an action that runs onSelect.
    return (
        <BaseMenu.Item className={rowClass} aria-current={ariaCurrent} disabled={props.disabled} closeOnClick={closeOnSelect} onClick={props.onSelect}>
            {content}
        </BaseMenu.Item>
    );
};

/** A setting to turn on or off. */
export const MenuCheckboxItem = (props: {
    children: ReactNode;
    checked: boolean;
    onChange: (checked: boolean) => void;
    icon?: ReactNode;
    count?: number;
    disabled?: boolean;
    /** Defaults to false. */
    closeOnSelect?: boolean;
}) => (
    <BaseMenu.CheckboxItem
        className={FLOATING_ROW_CLASS}
        checked={props.checked}
        onCheckedChange={checked => props.onChange(checked)}
        disabled={props.disabled}
        closeOnClick={props.closeOnSelect === true}
    >
        <FloatingRowContent
            leading={<Checkbox checked={props.checked} disabled={props.disabled} />}
            icon={props.icon}
            trailing={props.count !== undefined ? <FloatingRowCount count={props.count} /> : undefined}
        >
            {props.children}
        </FloatingRowContent>
    </BaseMenu.CheckboxItem>
);

/** Group of MenuRadioItems, where one is chosen. */
export const MenuRadioGroup = <TValue extends string | number>(props: {
    value: TValue;
    onChange: (value: TValue) => void;
    children: ReactNode;
}) => (
    <BaseMenu.RadioGroup className={FLOATING_ROW_GROUP_CLASS} value={props.value} onValueChange={value => props.onChange(value as TValue)}>
        {props.children}
    </BaseMenu.RadioGroup>
);

/** One choice in a MenuRadioGroup. */
export const MenuRadioItem = (props: {
    value: string | number;
    children: ReactNode;
    icon?: ReactNode;
    count?: number;
    disabled?: boolean;
    /** Defaults to false. Set it when the menu holds only this choice, such as a sort menu. */
    closeOnSelect?: boolean;
}) => (
    <BaseMenu.RadioItem
        className={twMerge(FLOATING_ROW_CLASS, FLOATING_CHOSEN_ROW_CLASS)}
        value={props.value}
        disabled={props.disabled}
        closeOnClick={props.closeOnSelect === true}
    >
        <FloatingRowContent
            icon={props.icon}
            trailing={
                <>
                    {props.count !== undefined && <FloatingRowCount count={props.count} />}
                    <BaseMenu.RadioItemIndicator className="h-4 w-4">
                        <Check className="h-4 w-4 stroke-stone-600" />
                    </BaseMenu.RadioItemIndicator>
                </>
            }
        >
            {props.children}
        </FloatingRowContent>
    </BaseMenu.RadioItem>
);

/** Groups items under a heading. */
export const MenuSection = (props: { title: string; children: ReactNode }) => (
    <BaseMenu.Group className={FLOATING_ROW_GROUP_CLASS}>
        <BaseMenu.GroupLabel className={FLOATING_SECTION_TITLE_CLASS}>{props.title}</BaseMenu.GroupLabel>
        {props.children}
    </BaseMenu.Group>
);

/** A horizontal line between groups of items. */
export const MenuSeparator = () => <BaseMenu.Separator className={FLOATING_SEPARATOR_CLASS} />;
